import axiosV2Instance from './axiosV2Instance';
import axios from 'axios';
import { Media, MediaSize } from './usersApi';

export interface SiteV2 {
  id: number;
  name: string;
  closed: boolean;
  objectAddress?: string;
  customerName?: string;
  avatarUrl?: string; // URL аватарки объекта
}

// Простой кэш для объектов (в памяти, на время сессии)
interface CacheItem {
  data: SiteV2;
  timestamp: number;
}

// Кеш для списка всех объектов пользователя
interface MySitesCache {
  data: SiteV2[];
  timestamp: number;
}

const sitesCache = new Map<number, CacheItem>();
let mySitesCache: MySitesCache | null = null;
const SITE_CACHE_DURATION = 60000; // 60 секунд
const MY_SITES_CACHE_DURATION = 60000; // 60 секунд

export interface CreateSiteV2Request {
  name: string;
  closed?: boolean;
  objectAddress?: string;
  customerName?: string;
}

export interface UpdateSiteV2Request {
  name: string;
  closed: boolean;
  objectAddress?: string;
  customerName?: string;
}

export interface SiteRole {
  id: number;
  name: string;
  description: string;
}

export interface SiteMemberRole {
  id: number;
  siteMemberId: number;
  roleId: number;
  
  // Назначение роли
  addedAt: string;
  addedBy: number;
  addedReason?: string;
  
  // Отзыв роли
  revokedAt?: string;
  revokedBy?: number;
  revokedReason?: string;
  
  // Дополнительные поля
  roleName?: string;
  roleDescription?: string;
  hierarchyLevel?: number; // Уровень иерархии роли (чем меньше, тем выше)
  addedByUserName?: string;
  revokedByUserName?: string;
  
  isActive: boolean;
}

export interface SiteMember {
  id: number;
  siteId: number;
  userId: number;
  
  /** @deprecated Use roles property instead for multiple roles support */
  roleId?: number;
  
  userName?: string;
  
  /** @deprecated Use roles property instead for multiple roles support */
  roleName?: string;
  
  joinedAt: string;
  leftAt?: string;
  isActive: boolean;
  avatarUrl?: string; // URL аватарки пользователя

  /** Кто добавил участника (userId) */
  addedBy?: number;
  /** Кто исключил участника (userId) */
  removedBy?: number;
  /** Имя пользователя, который добавил/пригласил */
  addedByUserName?: string;
  /** Имя пользователя, который исключил */
  removedByUserName?: string;
  
  // Новое: список ролей участника
  roles?: SiteMemberRole[];
}

class SitesV2Api {
  async getAllSites(): Promise<SiteV2[]> {
    try {
      const response = await axiosV2Instance.get<SiteV2[]>('/SitesV2');
      return response.data;
    } catch (error) {
      console.error('Error fetching sites:', error);
      throw error;
    }
  }

  async getMySites(): Promise<SiteV2[]> {
    try {
      const response = await axiosV2Instance.get<SiteV2[]>('/SitesV2');
      const sites = response.data;
      
      // Кешируем все объекты из списка для быстрого доступа
      sites.forEach(site => {
        sitesCache.set(site.id, {
          data: site,
          timestamp: Date.now()
        });
      });
      
      // Кешируем весь список
      mySitesCache = {
        data: sites,
        timestamp: Date.now()
      };
      
      return sites;
    } catch (error) {
      console.error('Error fetching my sites:', error);
      throw error;
    }
  }

  /**
   * Получить кешированный список объектов пользователя
   * Возвращает null, если кеш пуст или устарел
   */
  getCachedMySites(): SiteV2[] | null {
    if (!mySitesCache) {
      return null;
    }
    
    const now = Date.now();
    if ((now - mySitesCache.timestamp) < MY_SITES_CACHE_DURATION) {
      return mySitesCache.data;
    }
    
    // Кеш устарел
    mySitesCache = null;
    return null;
  }

  async getSite(id: number, useCache: boolean = true): Promise<SiteV2> {
    // Проверяем кеш
    if (useCache) {
      const cached = sitesCache.get(id);
      const now = Date.now();
      
      if (cached && (now - cached.timestamp) < SITE_CACHE_DURATION) {
        // Возвращаем данные из кеша
        return cached.data;
      }
    }
    
    try {
      const response = await axiosV2Instance.get<SiteV2>(`/SitesV2/${id}`);
      const site = response.data;
      
      // Сохраняем в кеш
      sitesCache.set(id, {
        data: site,
        timestamp: Date.now()
      });
      
      return site;
    } catch (error) {
      console.error('Error fetching site:', error);
      throw error;
    }
  }

  /**
   * Инвалидировать кеш для объекта (использовать при изменении объекта)
   */
  invalidateSiteCache(id: number): void {
    sitesCache.delete(id);
  }

  /**
   * Очистить весь кеш объектов
   */
  clearSiteCache(): void {
    sitesCache.clear();
    mySitesCache = null;
  }

  async createSite(siteData: CreateSiteV2Request): Promise<SiteV2> {
    try {
      const response = await axiosV2Instance.post<SiteV2>('/SitesV2', siteData);
      return response.data;
    } catch (error) {
      console.error('Error creating site:', error);
      throw error;
    }
  }

  async updateSite(id: number, siteData: UpdateSiteV2Request): Promise<void> {
    try {
      // Сервер ожидает Id в теле (должен совпадать с id в маршруте)
      await axiosV2Instance.put(`/SitesV2/${id}`, { id, ...siteData });
      
      // Инвалидируем кеш после обновления объекта
      this.invalidateSiteCache(id);
    } catch (error) {
      console.error('Error updating site:', error);
      throw error;
    }
  }

  async deleteSite(id: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`/SitesV2/${id}`);
    } catch (error) {
      console.error('Error deleting site:', error);
      throw error;
    }
  }

  async getSiteRoles(siteId: number): Promise<SiteRole[]> {
    try {
      const response = await axiosV2Instance.get<SiteRole[]>(`/SitesV2/${siteId}/roles`);
      return response.data;
    } catch (error) {
      console.error('Error fetching site roles:', error);
      throw error;
    }
  }

  async getSiteMembers(siteId: number): Promise<SiteMember[]> {
    try {
      const response = await axiosV2Instance.get<SiteMember[]>(`/SitesV2/${siteId}/members`);
      return response.data;
    } catch (error) {
      console.error('Error fetching site members:', error);
      throw error;
    }
  }

  async removeSiteMember(siteId: number, userId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`/SitesV2/${siteId}/members/${userId}`);
    } catch (error) {
      console.error('Error removing site member:', error);
      throw error;
    }
  }

  async uploadAvatar(siteId: number, file: File): Promise<Media> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axiosV2Instance.post<Media>(
        `/SitesV2/${siteId}/avatar`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      // Инвалидируем кеш после загрузки аватарки
      this.invalidateSiteCache(siteId);
      return response.data;
    } catch (error) {
      console.error('Error uploading site avatar:', error);
      throw error;
    }
  }

  async getAvatar(siteId: number): Promise<Media | null> {
    try {
      const response = await axiosV2Instance.get<Media>(`/SitesV2/${siteId}/avatar`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Error getting site avatar:', error);
      throw error;
    }
  }

  async getSiteAvatars(siteId: number): Promise<Media[]> {
    try {
      const response = await axiosV2Instance.get<Media[]>(`/SitesV2/${siteId}/avatars`);
      return response.data;
    } catch (error) {
      console.error('Error getting site avatars:', error);
      throw error;
    }
  }

  async deleteAvatar(siteId: number, mediaId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`/SitesV2/${siteId}/avatar/${mediaId}`);
      // Инвалидируем кеш после удаления аватарки
      this.invalidateSiteCache(siteId);
    } catch (error) {
      console.error('Error deleting site avatar:', error);
      throw error;
    }
  }

  async activateAvatar(siteId: number, mediaId: number): Promise<void> {
    try {
      await axiosV2Instance.post(`/SitesV2/${siteId}/avatar/${mediaId}/activate`);
      // Инвалидируем кеш после активации аватарки
      this.invalidateSiteCache(siteId);
    } catch (error) {
      console.error('Error activating site avatar:', error);
      throw error;
    }
  }

  // ============================================
  // МЕТОДЫ ДЛЯ РАБОТЫ С МНОЖЕСТВЕННЫМИ РОЛЯМИ
  // ============================================

  /**
   * Получить все активные роли участника объекта
   */
  async getSiteMemberRoles(siteId: number, siteMemberId: number): Promise<SiteMemberRole[]> {
    try {
      const response = await axiosV2Instance.get<SiteMemberRole[]>(
        `/SitesV2/${siteId}/members/${siteMemberId}/roles`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching site member roles:', error);
      throw error;
    }
  }

  /**
   * Получить полную историю ролей участника объекта (активные + отозванные)
   */
  async getSiteMemberRoleHistory(siteId: number, siteMemberId: number): Promise<SiteMemberRole[]> {
    try {
      const response = await axiosV2Instance.get<SiteMemberRole[]>(
        `/SitesV2/${siteId}/members/${siteMemberId}/roles/history`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching site member role history:', error);
      throw error;
    }
  }

  /**
   * Назначить роль участнику объекта
   */
  async assignRole(
    siteId: number, 
    siteMemberId: number, 
    roleId: number, 
    reason?: string
  ): Promise<{ id: number }> {
    try {
      const response = await axiosV2Instance.post<{ id: number }>(
        `/SitesV2/${siteId}/members/${siteMemberId}/roles`,
        { roleId, reason }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning role:', error);
      throw error;
    }
  }

  /**
   * Отозвать роль у участника объекта
   */
  async revokeRole(
    siteId: number, 
    siteMemberId: number, 
    roleRecordId: number, 
    reason?: string
  ): Promise<void> {
    try {
      await axiosV2Instance.delete(
        `/SitesV2/${siteId}/members/${siteMemberId}/roles/${roleRecordId}`,
        { data: { reason } }
      );
    } catch (error) {
      console.error('Error revoking role:', error);
      throw error;
    }
  }

  // ============================================
  // МЕТОДЫ ДЛЯ РАБОТЫ С ПРИГЛАШЕНИЯМИ
  // ============================================

  /**
   * Создать новое приглашение для объекта.
   * targetSiteExecutorId — именное приглашение на место внешнего исполнителя.
   */
  async createInvitation(siteId: number, targetSiteExecutorId?: number): Promise<{
    id: number;
    token: string;
    inviteUrl: string;
    expiresAt: string;
    targetSiteExecutorId?: number | null;
    targetExecutorName?: string | null;
  }> {
    try {
      const response = await axiosV2Instance.post<{
        id: number;
        token: string;
        inviteUrl: string;
        expiresAt: string;
        targetSiteExecutorId?: number | null;
        targetExecutorName?: string | null;
      }>(`/SitesV2/${siteId}/invitations`, undefined, {
        params: targetSiteExecutorId ? { targetSiteExecutorId } : undefined,
      });
      return response.data;
    } catch (error) {
      console.error('Error creating invitation:', error);
      throw error;
    }
  }

  /**
   * Получить информацию о приглашении по токену (публичный метод)
   */
  async getInvitationInfo(token: string): Promise<{
    siteId: number;
    siteName: string;
    createdByName: string;
    expiresAt: string;
    isValid: boolean;
    targetExecutorName?: string | null;
  }> {
    try {
      // Публичный запрос без авторизации
      const baseURL = axiosV2Instance.defaults.baseURL || 'http://localhost:5000/api/v2';
      const response = await axios.get<{
        siteId: number;
        siteName: string;
        createdByName: string;
        expiresAt: string;
        isValid: boolean;
        targetExecutorName?: string | null;
      }>(`${baseURL}/invitations/${token}`);
      return response.data;
    } catch (error) {
      console.error('Error getting invitation info:', error);
      throw error;
    }
  }

  /**
   * Принять приглашение (вступить в объект)
   */
  async acceptInvitation(token: string): Promise<{ memberId: number; message: string }> {
    try {
      const response = await axiosV2Instance.post<{ memberId: number; message: string }>(
        `/invitations/${token}/accept`
      );
      return response.data;
    } catch (error) {
      console.error('Error accepting invitation:', error);
      throw error;
    }
  }
}

export const sitesV2Api = new SitesV2Api();
